import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "tw3-flex tw3-gap-3 tw3-border-b tw3-border-surface-200 tw3-pb-4"
}
const _hoisted_2 = { class: "tw3-font-semibold" }
const _hoisted_3 = {
  key: 0,
  class: "tw3-mt-0.5 tw3-text-xs tw3-tracking-wider tw3-text-surface-400"
}
const _hoisted_4 = {
  key: 1,
  class: "tw3-m-0 tw3-mt-4 tw3-grid tw3-list-none tw3-gap-2.5 tw3-p-0"
}
const _hoisted_5 = {
  key: 0,
  class: "tw3-mt-0.5 tw3-block tw3-text-xs tw3-text-surface-400"
}
const _hoisted_6 = {
  key: 2,
  class: "tw3-mt-4 tw3-flex tw3-items-center tw3-justify-between tw3-border-t tw3-border-surface-200 tw3-pt-4 tw3-font-semibold"
}
const _hoisted_7 = { class: "tw3-text-2xl tw3-font-extrabold tw3-text-primary-600" }
const _hoisted_8 = {
  key: 3,
  class: "-tw3-mx-3 tw3-mt-3 tw3-flex tw3-items-center tw3-justify-between tw3-gap-3 tw3-rounded-xl tw3-bg-primary-50 tw3-px-3 tw3-py-2.5 tw3-font-semibold tw3-text-primary-700"
}
const _hoisted_9 = { class: "tw3-text-lg tw3-font-extrabold" }

import { computed } from 'vue';

import { useBookingQuoteStore } from '@/entities/quickQuote/lib/booking-store';
import { formatMoney } from '@/shared/helpers/formatters';

import BookingCard from './BookingCard.vue';

import type { EstimateCost } from '@/entities/quickQuote/lib/booking-api.types';

type SummaryLineItem = {
	id: string;
	label: string;
	note?: string;
	cost: EstimateCost;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'BookingQuoteSummary',
  setup(__props) {

const bookingStore = useBookingQuoteStore();

const formatChipCount = (count: number) =>
	`${count} ${count === 1 ? 'chip' : 'chips'}`;

const vehicle = computed(() => bookingStore.selectedVehicle);
const lineItems = computed<SummaryLineItem[]>(() => {
	const glass = bookingStore.selectedGlass;
	return [
		...(glass
			? [
					{
						id: `glass:${glass.key}`,
						label: glass.label,
						note:
							bookingStore.selectedGlassOption === 'repair'
								? formatChipCount(bookingStore.chipCount)
								: undefined,
						cost: bookingStore.glassCost
					}
				]
			: []),
		...bookingStore.requiredOptions.map(option => ({
			id: `required:${option.key}`,
			label: option.label,
			cost: bookingStore.getOptionCost(option)
		})),
		...bookingStore.selectedExtraOptions.map(option => ({
			id: `extra:${option.key}`,
			label: option.label,
			cost: bookingStore.getOptionCost(option)
		}))
	];
});
const total = computed(() => bookingStore.quoteTotal);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(BookingCard, { class: "tw3-p-5" }, {
    default: _withCtx(() => [
      _cache[2] || (_cache[2] = _createElementVNode("h3", { class: "tw3-mb-4 tw3-text-xs tw3-font-bold tw3-uppercase tw3-tracking-wider tw3-text-surface-500" }, " Quote summary ", -1)),
      (vehicle.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _cache[0] || (_cache[0] = _createElementVNode("i", { class: "pi pi-car tw3-mt-0.5 tw3-text-primary-600" }, null, -1)),
            _createElementVNode("div", null, [
              _createElementVNode("div", _hoisted_2, _toDisplayString(vehicle.value.year) + " " + _toDisplayString(vehicle.value.make) + " " + _toDisplayString(vehicle.value.model), 1),
              (_unref(bookingStore).vehicleVin)
                ? (_openBlock(), _createElementBlock("div", _hoisted_3, " VIN " + _toDisplayString(_unref(bookingStore).vehicleVin), 1))
                : _createCommentVNode("", true)
            ])
          ]))
        : _createCommentVNode("", true),
      (lineItems.value.length)
        ? (_openBlock(), _createElementBlock("ul", _hoisted_4, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(lineItems.value, (item) => {
              return (_openBlock(), _createElementBlock("li", {
                key: item.id,
                class: "tw3-flex tw3-items-start tw3-justify-between tw3-gap-3 tw3-text-sm tw3-text-surface-700"
              }, [
                _createElementVNode("span", null, [
                  _createTextVNode(_toDisplayString(item.label) + " ", 1),
                  (item.note)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(item.note), 1))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("span", null, _toDisplayString(_unref(formatMoney)(item.cost.total)), 1)
              ]))
            }), 128))
          ]))
        : _createCommentVNode("", true),
      (total.value !== null)
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
            _cache[1] || (_cache[1] = _createElementVNode("span", null, "Total", -1)),
            _createElementVNode("span", _hoisted_7, _toDisplayString(_unref(formatMoney)(total.value)), 1)
          ]))
        : _createCommentVNode("", true),
      _cache[3] || (_cache[3] = _createElementVNode("p", { class: "tw3-mt-1 tw3-text-right tw3-text-[0.7rem] tw3-text-surface-400" }, " Tax included ", -1)),
      (total.value !== null && _unref(bookingStore).paymentRequired)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
            _createElementVNode("span", null, "Due today " + _toDisplayString(_unref(bookingStore).dueTodayPercent), 1),
            _createElementVNode("span", _hoisted_9, _toDisplayString(_unref(formatMoney)(_unref(bookingStore).prepaymentAmount || 0)), 1)
          ]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})